/**
 * UI 组件工厂 — 生成带统一样式 class 的 DOM 元素
 * 视觉基准：docs/UI规范.md §2.1/§3 + 本文件；原型 ui-concept/*.html 已归档（2026-10-24）
 */
import { AttributeKey } from '../data/types';
import { GameData } from '../data/GameData';
import { icon, iconAny, IconName } from './icons';
import { setNum } from './feedback';
import { fillAvatarSlot } from '../avatar/gamemount';
import { rankSystem } from '../systems/RankSystem';
import { GameConfig } from '../core/GameConfig';
import { Utils } from '../core/Utils';
import type { GameState } from '../systems/GameState';
import { attachClickSound, playClickSound } from '../audio/AudioManager';

/** 属性显示名（对应 attributes.ts 的 name 字段，此处便捷映射） */
export function attrName(key: AttributeKey): string {
    return GameData.ATTRIBUTE_DEFS[key].name;
}

/**
 * 效果键的显示名（2026-10-15 由 `MonthProgressPanel.effectLabel` 提到公共处）：
 * 随机事件、行程、剧情三处变动行共用一把尺——同一键在哪个页面上都读同一个词。
 * 键表与 `eventApplyEffects` 的 switch 同源；查无（配错的键）原样显示，看得见才好改。
 */
export function effectLabel(key: string): string {
    const map: Record<string, string> = { money: '金钱', energy: '体力', fame: '名气', reputation: '口碑', rank: '咖位' };
    if (map[key]) return map[key];
    try { return attrName(key as AttributeKey); } catch { return key; }
}

/**
 * 变动行的一枚 `.chg`（主体名 + 带符号增量，正收益走成功色、负收益走失败色）。
 * VN 对话框、行程文案、聊天页气泡三处共用同一份画法（2026-10-15 收口，不许各写一遍）。
 * 行容器由调用方自备（`.changes`：VN 用常驻那只、聊天页每颗气泡新建一只）。
 */
export function appendChanges(row: HTMLElement, changes: { label: string; delta: string }[]): void {
    changes.forEach(c => {
        const span = createSpan('chg', `${c.label} `);
        span.appendChild(createSpan(`delta-in ${c.delta.startsWith('-') ? 'text-fail' : 'text-success'}`, c.delta));
        row.appendChild(span);
    });
}

/**
 * 剧情文案高亮标记 `[[文字]]` 的唯一解析口（2026-10-16 茄子令：特殊剧情里出现这个标记，不分载体都要上色）。
 * 以前这串只有 VnDialog 认，且「点屏跳过打字机」那条出口只塞了原始串，所以手机上看成中括号本字。
 * 现在三载体共用这一处：`encounter` 走 VN（正文＋选项）、`message` 走聊天页（气泡＋待办行）、`media` 走弹窗（标题＋正文＋评论）。
 *  - `plotPlain`：剥掉标记的纯文本（打字机逐字打这句用）
 *  - `plotHtml` ：转义后把标记换成 `<mark class="plot-hl">`，交 innerHTML
 *  - `setPlotText`：把解析结果直接写进一只现成节点（一次建正文处用，返回同一只节点好串进 appendChild）
 * 注意：只有 `<mark>` 这一枚标签由本函数插入，其余原文先转义，数据表里的文案不许绕过这里直塞 innerHTML。
 * 不成对的标记（写了 `[[` 忘了 `]]`）按原样显示——那是文案的笔误，归《填写格式.md》的自查，不在这里兜。
 */
const PLOT_MARK_RE = /\[\[([^\]]+)\]\]/g;
export function plotPlain(t: string): string {
    return t.replace(PLOT_MARK_RE, '$1');
}
export function plotHtml(t: string): string {
    return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(PLOT_MARK_RE, '<mark class="plot-hl">$1</mark>');
}
export function setPlotText(el: HTMLElement, t: string): HTMLElement {
    el.innerHTML = plotHtml(t);
    return el;
}

/** 创建 div */
export function createDiv(className?: string, children?: (HTMLElement | string)[]): HTMLDivElement {
    const el = document.createElement('div');
    if (className) el.className = className;
    if (children) appendChildren(el, children);
    return el;
}

/** 创建 span */
export function createSpan(className?: string, text?: string): HTMLSpanElement {
    const el = document.createElement('span');
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
}

/** 创建按钮。传 iconName 时按「图标 + 文字」排布（D-05，容器需为 flex） */
export function createButton(className: string, text: string, onClick?: () => void, iconName?: IconName): HTMLButtonElement {
    const el = document.createElement('button');
    el.type = 'button';
    if (className) el.className = className;
    if (iconName) el.appendChild(icon(iconName));
    if (text) el.appendChild(createSpan(undefined, text));
    // 点击音走共享助手的**幂等独立监听**（2026-10-24 音频批，与 createBtn / makeClickable 同一套）：
    // 实测踩过的坑——本工厂有大量 `createButton(cls, text, undefined, icon)` 用法（MainMenuPanel 的
    // 三个主按钮、WeiboPanel、GameOverPanel 都是），onClick 为 undefined，调用方再自己 addEventListener，
    // 若把音效包在 onClick 里就整颗按钮无声。独立监听 + 按事件 timeStamp 去重才覆盖全量用法，
    // 且原生 button 的键盘激活同样派发 click，鼠标与键盘两条路都有声
    attachClickSound(el);
    if (onClick) el.addEventListener('click', onClick);
    return el;
}

/**
 * 遮罩即关闭钮（2026-10-16 弹窗统一批·茄子令「删掉卡内那颗纯关闭/知道了，改成点空白关闭」）。
 *
 * 为什么不能一删了之：纯只读弹窗删掉那颗按钮后，卡里就没有任何可聚焦元素了，而 modal.ts 的入栈
 * 判据是 `focusables(el).length`——不入栈就没有 role=dialog / aria-modal / aria-label，
 * Esc 走不到这一层、Tab 能溜到弹窗背后的页面控件上（正是 H-47 那批专门修的腿）。
 * 所以关闭热区自己必须可聚焦：把它做成遮罩本体，点它＝点空白＝关闭，Tab 停到它，
 * Esc 复用 modal.ts 现成的 `.btn-cancel` 分支（dismiss 里第一顺位就是它，data-esc 那条兜底随之作废），
 * 读屏念得出「关闭」。视觉上零新增——它就是那层遮罩。
 *
 * 用法：先挂热区再挂卡（`wrap.appendChild(dismiss); wrap.appendChild(card);`），热区必须是 wrap 的第一个子节点，
 * 让 `.modal-wrap > .dimoverlay`（z0）压在 `.modal-note-card`（wrap 层叠上下文内的局部档 z5）下面。
 * 挂了它之后，原先那条「点 wrap 空白关闭」的监听器就成死码，同批删掉——两处重叠的关闭面比一处更容易出错。
 *
 * `painted: false` 给那些背景另有用途、不该再叠一层淡底的卡（目前只有颁奖夜现场版：它的结果卡坐在
 * 颁奖夜场景图上，refresh() 里本来就显式撤过遮罩）。这种关闭热区是透明的一颗，形状走 `.modal-dismiss`。
 */
export function createModalDismiss(onClose: () => void, opts: { painted?: boolean } = {}): HTMLButtonElement {
    const el = createButton(opts.painted === false ? 'modal-dismiss btn-cancel' : 'dimoverlay btn-cancel', '', onClose);
    el.setAttribute('aria-label', '关闭');
    return el;
}

/**
 * 创建语义按钮（H-40）。与 createButton 的区别：子节点原样喂给 appendChildren，
 * 不额外包 span，供「页签 / 胶囊 / 槽位」这类外观完全由 className 决定的控件使用。
 * 外观靠 class 决定，UA 默认样式的复位集中在 ui.css 的「H-40 语义化复位」块。
 */
export function createBtn(className?: string, children?: (HTMLElement | string)[]): HTMLButtonElement {
    const el = document.createElement('button');
    el.type = 'button';
    if (className) el.className = className;
    if (children) appendChildren(el, children);
    // 本工厂没有 onClick 参数（监听由调用方后挂）：点击音走共享助手的独立监听，
    // 幂等绑定防重复；同一次 click 沿冒泡链只响一声（见 AudioManager.attachClickSound 的 timeStamp 去重）
    attachClickSound(el);
    return el;
}

/**
 * 给「含块级子节点的可点击条目」补键盘可达性（H-40）。
 * 这类条目不能换成 <button>——button 的内容模型只允许 phrasing content，行内的 <div>
 * 子节点属于非法嵌套；故保留 <div role="button" tabindex="0"> 并把 Enter / Space 接到同一个 handler。
 */
export function makeClickable<T extends HTMLElement>(el: T, onClick: () => void): T {
    el.setAttribute('role', 'button');
    el.tabIndex = 0;
    // click 路径用共享助手的幂等独立监听（与 createButton / createBtn 同一个，同一次派发只响一声）
    attachClickSound(el);
    el.addEventListener('click', onClick);
    el.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();  // 空格默认滚动页面，会吞掉激活
        playClickSound(el);  // 键盘路径不派发 click：声音必须在这里显式补上（2026-10-24 音频批）
        onClick();
    });
    return el;
}

/**
 * 重设「图标 + 文字」标签（按钮状态变化时用，如 签约 → 已签约）。
 * iconAfter=true 时图标置于文字之后——前进类按钮（「继续 ▶」）的箭头语义。
 */
export function setLabel(el: HTMLElement, text: string, iconName?: IconName, iconAfter = false): void {
    clearChildren(el);
    const glyph = iconName ? icon(iconName) : null;
    if (glyph && !iconAfter) el.appendChild(glyph);
    if (text) el.appendChild(createSpan(undefined, text));
    if (glyph && iconAfter) el.appendChild(glyph);
}

/**
 * 把文本里的 💎 占位符替换为内联宝石 SVG（成就点货币，D-05 C 类）。
 * 用于「图标夹在文字流中间」的块级文本容器（商店分组标题 / 成就点标签 / 备注），
 * 这类位置无法走 createButton。返回可直接喂给 appendChildren 的子节点数组。
 */
export function gemText(text: string): (HTMLElement | string)[] {
    const out: (HTMLElement | string)[] = [];
    text.split('💎').forEach((seg, i) => {
        if (i > 0) out.push(icon('gem'));
        if (seg) out.push(seg);
    });
    return out;
}

/** 创建虚线分隔 */
export function createHr(className = 'divider-dotted'): HTMLHRElement {
    const el = document.createElement('hr');
    el.className = className;
    return el;
}

/** 批量追加子节点（支持文本节点） */
export function appendChildren(parent: HTMLElement, children: (HTMLElement | string)[]): void {
    for (const child of children) {
        if (typeof child === 'string') {
            parent.appendChild(document.createTextNode(child));
        } else {
            parent.appendChild(child);
        }
    }
}

/** 清空元素 */
export function clearChildren(el: HTMLElement): void {
    el.innerHTML = '';
}

/** 属性条的跨刷新引用（G-36：条子和数值要存活，动画才有起点） */
export interface StatBarParts {
    row: HTMLElement;
    fill: HTMLElement;
    value: HTMLElement;
}

/** 属性条：label + 进度条 + 数值，返回可复用引用。对应 UIFactory.makeStatRow */
export function createStatBarParts(label: string, value: number, max: number): StatBarParts {
    const row = createDiv('stat-row');
    row.appendChild(createSpan('stat-label', label));
    const barBg = createDiv('stat-bar-bg');
    const fill = createDiv('stat-bar-fill');
    const valueEl = createSpan('stat-value', String(value));
    applyStatBarState(fill, valueEl, value, max);
    barBg.appendChild(fill);
    row.appendChild(barBg);
    row.appendChild(valueEl);
    return { row, fill, value: valueEl };
}

/**
 * 负值画法（2026-10-04 用户定，口碑可负批）：**尺子不动**，条长按绝对值走、0 仍是空条，
 * 负值时填充与读数一起染红。⇒ 口碑 -200 与 +200 一样长，方向只由颜色和符号表达。
 * 只有口碑能穿负（其余七维 min 仍是 0），所以本分支⛔ 影响其他属性的观感。
 * 建条与更新都走这一个函数（否则新建态与复用态的画法会分叉）。
 */
function applyStatBarState(fill: HTMLElement, valueEl: HTMLElement, value: number, max: number): void {
    const neg = value < 0;
    fill.style.width = `${statPct(value, max)}%`;
    fill.classList.toggle('is-neg', neg);
    valueEl.classList.toggle('is-neg', neg);
    if (!neg) valueEl.textContent = String(value);   // 文本只在初建时写；setStatBar 走 setNum 动画
}

function statPct(value: number, max: number): number {
    return Math.max(0, Math.min(100, (Math.abs(value) / max) * 100));
}

/** 更新一条复用中的属性条：数值走 G-36 滚动+脉冲，条子靠 .stat-bar-fill 的 width 过渡生长。
    密集网格里关掉飘字——8 格同帧各飘一个会互相压字，条子生长本身已在报涨跌。 */
export function setStatBar(parts: StatBarParts, value: number, max: number): void {
    setNum(parts.value, value, { float: false });
    applyStatBarState(parts.fill, parts.value, value, max);
}

/** 薄荷块（标题 + 内容容器） */
export function createMintBlock(title: string, content?: HTMLElement, extraClass = ''): HTMLElement {
    const block = createDiv(`mint-block ${extraClass}`.trim());
    block.appendChild(createSpan('mint-block-title', title));
    if (content) block.appendChild(content);
    return block;
}

/**
 * 行程槽位：周次 + 名称。
 * @param state 'filled' 已安排（薄荷实底）｜'target' 下一次安排的落点（描边高亮）｜'idle' 空且非落点
 * 对应原型 .slot / .slot.filled（原型已归档 2026-10-24，现行值见 ui.css）
 */
export function createSlot(index: number, label: string, state: 'filled' | 'target' | 'idle' = 'idle', extraClass = ''): HTMLElement {
    const cls = state === 'filled' ? 'filled' : state === 'target' ? 'target' : '';
    return createBtn(`slot ${cls} ${extraClass}`.trim(), [
        createSpan('week-no', `第${index + 1}周`),
        createSpan('slot-name', label),
    ]);
}

/** 分类页签（课程/工作/休息） */
export function createCatTab(label: string, active: boolean): HTMLElement {
    const tab = createBtn(`cat-tab ${active ? 'active' : ''}`.trim());
    tab.textContent = label;
    return tab;
}

/** 标签芯片 */
export function createTagChip(text: string, type: 'positive' | 'negative' | 'neutral'): HTMLElement {
    const cls = type === 'positive' ? 'pos' : type === 'negative' ? 'neg' : '';
    return createSpan(`tag-chip ${cls}`.trim(), text);
}

/**
 * info 图标 + 点击弹出说明气泡（前端设计原则 2026-10-28 的「补充信息」正路：
 * 布局表达不了的机制说明挂这里，替代常驻的次要说明行）。
 * 原生 title 在触屏上不弹，所以做成点击开合的气泡；全局同时只留一个打开的，
 * 点击图标与气泡以外的任何地方收起（点外收起的监听全页只挂一份，靠 data 标记防重）。
 */
export function createInfoTip(text: string): HTMLElement {
    const tip = createSpan('info-tip');
    const btn = createBtn('info-tip-btn');
    btn.setAttribute('aria-label', '说明');
    btn.appendChild(iconAny('helpFill'));
    const bubble = createSpan('info-tip-bubble', text);
    btn.addEventListener('click', () => {
        document.querySelectorAll('.info-tip.open').forEach(el => {
            if (el !== tip) el.classList.remove('open');
        });
        tip.classList.toggle('open');
    });
    tip.appendChild(btn);
    tip.appendChild(bubble);
    if (!document.documentElement.dataset.infoTipBound) {
        document.documentElement.dataset.infoTipBound = '1';
        // 捕获阶段：弹窗壳（ContractSignPop 等）在 click 里 stopPropagation 拦冒泡，
        // 冒泡阶段的点外监听收不到事件，气泡会关不掉——捕获段不受 stopPropagation 影响。
        document.addEventListener('click', (e) => {
            document.querySelectorAll('.info-tip.open').forEach(el => {
                if (!el.contains(e.target as Node)) el.classList.remove('open');
            });
        }, true);
    }
    return tip;
}

/** 一行数值变化（载体用；正负靠颜色也靠符号） */
export interface ValueChangeRow {
    /** 读数名（口碑／名气／粉丝／金钱…） */
    label: string;
    /** 带符号的增量文本（"+50"／"-50"；数值来源＝formatBeatEffects 的同一份清单） */
    delta: string;
}

/** toast 停留时长：普通提示与数值变化共用（2026-11-01 用户令：全站通知气泡同一套标准）；
 *  聊天流内的临时好感气泡也读这一档（`CharactersPanel.flashChatHint`）。 */
export const HINT_MS = 2200;

/** toast 单例渲染（只此一枚 `#app-hint`；后到的内容直接顶替先到的，⛔ 不并存第二枚气泡） */
function renderHint(children: (HTMLElement | string)[], ms: number): HTMLDivElement {
    let el = document.getElementById('app-hint') as HTMLDivElement | null;
    if (!el) {
        el = createDiv('hint hidden');
        el.id = 'app-hint';
        document.getElementById('app')?.appendChild(el);
    }
    clearChildren(el);
    el.style.top = '';
    el.style.bottom = '';                       // 清掉上一枚数值气泡的挂卡内联值，回到 CSS 标准位
    appendChildren(el, children);
    el.classList.remove('hidden');
    clearTimeout((el as HTMLDivElement & { _timer?: number })._timer);
    (el as HTMLDivElement & { _timer?: number })._timer = window.setTimeout(() => {
        el?.classList.add('hidden');
    }, ms);
    return el;
}

/**
 * **数值气泡的落点**（2026-10-22 用户令「媒体弹窗＋气泡这种情况，气泡可以挂在弹窗下方一点」）。
 *
 * 单例化后仍是同一枚 `#app-hint`，只有数值变化会走这层动态挂点；普通提示一律留在 CSS 标准位。
 * 为什么不能只靠 CSS：媒体卡高度随内容变（3 条热评 vs 10 条），固定 `bottom: 8rem` 在长卡上
 * 会压在卡片下沿（用户判「有点丑」）。这里现量卡片高度，挂在它下面 10px；
 * 下方放不下（卡片快满屏）就退回卡片上方，都放不下才夹到导航之上——⛔ 永远不压卡片边缘。
 *
 * ⚠️ **不许用 getBoundingClientRect 量卡片**：`.modal-wrap` 入场带 `transform: scale(0.93→1)`，
 * 开卡那一瞬量到的是缩放后的框（偏小）⇒ 气泡会落进卡片里（实测踩过）。改用**布局盒**：
 * 遮罩是 `inset:0` 的 flex 居中，卡高取 `offsetHeight`（不受 transform 影响），
 * 上下留白＝(容器高 − 卡高)/2。
 */
function placeValueChange(el: HTMLElement): void {
    const card = document.querySelector('.modal-wrap.show .media-card') as HTMLElement | null;
    if (!card) return;                          // 无媒体弹窗 ⇒ 维持 CSS 的 bottom: 8rem+sab
    const host = (el.offsetParent as HTMLElement | null) ?? document.getElementById('app');
    const hostH = host ? host.clientHeight : window.innerHeight;
    const cardH = card.offsetHeight;
    const cardTop = Math.max(0, (hostH - cardH) / 2);
    const cardBottom = cardTop + cardH;
    const h = el.offsetHeight || 36;
    const gap = 10;
    const navReserve = 76;                                  // 底部导航 ＋ 安全区，别挂到导航上
    const limit = hostH - navReserve;

    let top: number;
    if (cardBottom + gap + h <= limit) top = cardBottom + gap;        // 卡片下方（用户要的位置）
    else if (cardTop - gap - h >= 8) top = cardTop - gap - h;         // 放不下就卡上方
    else top = Math.max(8, limit - h);                                // 实在满屏：夹到导航之上
    el.style.top = `${Math.round(top)}px`;
    el.style.bottom = 'auto';
}

/**
 * **数值变化的载体**（2026-10-22 用户令「我们需要一个不出现对话框的情况下承载数值变化的载体」）。
 *
 * 2026-11-01 用户令：通知气泡全站只留一枚 —— 数值变化与普通提示**共用同一个 `#app-hint` 单例**，
 * 位置/层级/时长一律按普通提示（底部 8rem＋安全区、z 25、2200ms），⛔ 不再有第二枚气泡叠在上面。
 * 唯一例外＝**媒体弹窗在场时**气泡挂到卡片下沿再往下 10px（见 `placeValueChange`）。
 * ⛔ **只显示数值变化，不带事件名**（2026-10-22 用户令）。
 * 正负**既靠符号也靠颜色**：正＝--textMint、负＝--neg（⛔ 不用 --textFail：它在纸底上只有 2.48:1）。
 */
export function showValueChanges(rows: ValueChangeRow[], ms = HINT_MS): void {
    if (!rows.length) return;
    const el = renderHint(rows.map(r => {
        const span = createSpan('vc-delta ' + (r.delta.startsWith('-') ? 'neg' : 'pos'));
        span.appendChild(createSpan(undefined, r.label + ' '));
        span.appendChild(createSpan(undefined, r.delta));
        return span;
    }), ms);
    placeValueChange(el);
}

/** 简易 toast（`.hint` 单例，自动消失；文案里的 💎 渲染为宝石 SVG） */
export function showHint(text: string, ms = HINT_MS): void {
    renderHint(gemText(text), ms);
}


// ===== 执行页/决策日公共顶栏（2026-10-04 月度执行全屏化抽公共：两页 1:1 同构共用一份实现） =====

/** 公共顶栏引用集：root 三段（top-bar + res-compact + collapse-area），update 全量刷数值 */
export interface ExecTopBar {
    root: HTMLElement;
    avatarEl: HTMLElement;
    nameEl: HTMLElement;
    levelBadge: HTMLElement;
    dateBadge: HTMLElement;
    resEnergy: HTMLElement;
    resMoney: HTMLElement;
    toggleBar: HTMLButtonElement;
    collapseArea: HTMLElement;
    statBars: { key: AttributeKey; parts: StatBarParts }[];
    tagChips: HTMLElement;
    /** 刷新全部数值（G-36 滚动/条生长；属性行首建满后长期复用） */
    update(state: GameState): void;
}

/**
 * 顶栏咖位胶囊填充：咖位名 + 尾随问号（2026-10-28 茄子令）。
 * 胶囊本身是按钮（app.ts 委托监听，点开咖位阶梯弹窗），问号放在胶囊内表达「点开有详情」；
 * 图标首建常驻，之后只改文字 span——对胶囊直接 textContent 赋值会抹掉图标。
 * 执行页/人脉页/微博页三枚顶栏胶囊共用。
 */
export function fillLevelBadge(badge: HTMLElement, rankName: string): void {
    let txt = badge.querySelector('.lb-text') as HTMLElement | null;
    if (!txt) {
        badge.textContent = '';
        txt = createSpan('lb-text', rankName);
        badge.appendChild(txt);
        badge.appendChild(iconAny('helpFill'));
        return;
    }
    txt.textContent = rankName;
}

export function createExecTopBar(): ExecTopBar {    const topBar = createDiv('top-bar');
    const avatarEl = createDiv('avatar', ['👩‍🎤']);
    const info = createDiv('top-info');
    const nameEl = createSpan('p-name', '');
    const levelBadge = createSpan('level-badge', '');
    const dateBadge = createSpan('date-badge', '');
    info.appendChild(nameEl);
    info.appendChild(levelBadge);
    info.appendChild(dateBadge);
    topBar.appendChild(avatarEl);
    topBar.appendChild(info);

    const res = createDiv('res-compact');
    const energyItem = createSpan();
    energyItem.appendChild(icon('energy'));
    const resEnergy = createSpan('res-num');
    energyItem.appendChild(resEnergy);
    const moneyItem = createSpan();
    moneyItem.appendChild(icon('money'));
    const resMoney = createSpan('res-num');
    moneyItem.appendChild(resMoney);
    const toggleBar = createBtn('toggle-bar collapsed');
    const arrow = createSpan('arrow');
    arrow.appendChild(icon('chevronRight'));
    toggleBar.appendChild(arrow);
    toggleBar.appendChild(createSpan('label', '属性'));
    res.appendChild(energyItem);
    res.appendChild(moneyItem);
    res.appendChild(toggleBar);

    const collapseArea = createDiv('collapse-area');
    const statGrid = createDiv('stat-grid compact');
    collapseArea.appendChild(statGrid);
    const tagChips = createDiv('tag-chips');
    collapseArea.appendChild(tagChips);

    const root = createDiv('exec-top-wrap');
    root.appendChild(topBar);
    root.appendChild(res);
    root.appendChild(collapseArea);

    const statBars: { key: AttributeKey; parts: StatBarParts }[] = [];
    const attrMax = GameData.ATTRIBUTE_DEFS.talent.max;

    toggleBar.addEventListener('click', () => {
        const open = collapseArea.classList.toggle('open');
        toggleBar.classList.toggle('collapsed', !open);
        // 镜像类挂 wrap 上（`:has()` 低版本 WebView 不支持，C-06 同口径）：
        // 只有抽屉展开时顶栏下缘才要那两道圆弧（2026-10-12 茄子令「信息栏下面改直角」）
        root.classList.toggle('drawer-open', open);
    });

    /** 大额到账线（演出阈值，非经济数值，2026-10-07 正反馈批）：单次增量达此值顶栏走 strong 强演出 */
    const MONEY_STRONG_GAIN = 50000;
    let lastMoney: number | undefined;

    function update(state: GameState): void {
        nameEl.textContent = state.player.name;
        fillAvatarSlot(avatarEl, '👩‍🎤', state.player.avatar);   // 顶栏 .avatar = 3rem
        fillLevelBadge(levelBadge, rankSystem.getRankName(state.player.rank));
        dateBadge.textContent = `第${state.player.year + 1}年${state.player.month}月`;
        setNum(resEnergy, state.getEnergy(), { fmt: n => `体力 ${n}/${GameConfig.ENERGY_MAX}` });
        const money = state.getMoney();
        // 月度执行期间顶栏 update 不被调用，回决策页这一步 delta=整月累积 ⇒ 大月天然触发 strong
        setNum(resMoney, money, {
            fmt: n => `金钱 ¥${Utils.formatMoney(n)}`,
            deltaFmt: n => Utils.formatMoney(n),   // 飘字量与读数同口径（+8.0万 而非 +80000），符号由 feedback 自加
            strong: lastMoney !== undefined && money - lastMoney >= MONEY_STRONG_GAIN,
        });
        lastMoney = money;
        if (statBars.length === 0) {
            GameData.ATTRIBUTE_KEYS.forEach(key => {
                const attr = key as AttributeKey;
                const parts = createStatBarParts(attrName(attr), state.getAttribute(attr), attrMax);
                statGrid.appendChild(parts.row);
                statBars.push({ key: attr, parts });
            });
        } else {
            statBars.forEach(b => setStatBar(b.parts, state.getAttribute(b.key), attrMax));
        }
        tagChips.innerHTML = '';
        state.player.tags.forEach(tagId => {
            const tag = GameData.TAG_MAP[tagId] as import('../data/types').TagDef | undefined;
            if (tag) tagChips.appendChild(createTagChip(tag.name, tag.type));
        });
    }

    return {
        root, avatarEl, nameEl, levelBadge, dateBadge, resEnergy, resMoney,
        toggleBar, collapseArea, statBars, tagChips, update,
    };
}
